import React from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import HubHeader from "./HubHeader";
import HubSidebar from "./HubSidebar";
import "../../assets/css/Hub.css";
import { Toast } from "primereact/toast";
import { useTheme } from "../../contexts/ThemeContext";
import { useState, useMemo, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MomentumAILogoSmall from "../../assets/images/Momentum AI Icon-color (1).png";
import { getAllAMPs, AMPConfig } from "./AMP's/ampConfigs";
import { IconBaseProps } from "react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Skeleton } from "primereact/skeleton";

type Message = {
  sender: "user" | "bot";
  text: string;
  followups?: string[];
};

export default function SupportBotLanding() {

  const { accentColor } = useTheme();
  const toastRef = useRef<any>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { guid } = useParams();
  const [messages, setMessages] = useState<Message[]>([]);
  // Store uploaded files as objects with fileName and fileURL
  const [uploadedFiles, setUploadedFiles] = useState<{ fileName: string, fileURL: string }[]>([]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [showFooterCard, setShowFooterCard] = useState(true);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [ampOptions, setAmpOptions] = useState<AMPConfig[]>([]);
  const [selectedAmp, setSelectedAmp] = useState<AMPConfig | null>(null);
  const [showAmpPopover, setShowAmpPopover] = useState(false);
  const ampSelectorRef = useRef<HTMLDivElement | null>(null);
  const ampPopoverRef = useRef<HTMLDivElement | null>(null);
  const handleCollapseToggle = (next: boolean) => setCollapsed(next);
  const [ampHistory, setAmpHistory] = useState<any>(null);

  useEffect(() => {
    const loadAMPs = async () => {
      const amps = await getAllAMPs();
      setAmpOptions(amps);
      const supportAmp = amps.find(amp => amp.id === 'support-assistant');
      if (supportAmp) {
        setSelectedAmp(supportAmp);
      } else if (amps.length > 0) {
        setSelectedAmp(amps[0]);
      }
    };
    loadAMPs();
  }, []);

  React.useEffect(() => {
    setMessages([]);
    setInput("");
    setShowFooterCard(true);

    const fetchAmpHistory = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`https://datafabricapi-stage.insurancegig.com/api/momentummyamp/momentum-my-amp-history/${guid}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setAmpHistory(data);
      } catch (error) {
        setAmpHistory(null);
      }
    };
    fetchAmpHistory();

  }, [guid]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        showAmpPopover &&
        ampSelectorRef.current &&
        ampPopoverRef.current &&
        !ampSelectorRef.current.contains(target) &&
        !ampPopoverRef.current.contains(target)
      ) {
        setShowAmpPopover(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAmpPopover]);


  const handleSend = async () => {
    if (!input.trim()) return;

    const userText = input;

    setMessages((prev) => [...prev, { sender: "user", text: userText }]);
    scrollToBottom();
    setInput("");
    setShowFooterCard(false);

    let botIndex: number;

    setMessages((prev) => {
      botIndex = prev.length;
      return [...prev, { sender: "bot", text: "" }];
    });
    scrollToBottom();
    const formData = new FormData();
    formData.append("human_prompt", userText);
    formData.append("conversationId", guid || "");
    formData.append("nc_token", localStorage.getItem("amsToken") || "");
    formData.append("file_paths", JSON.stringify(uploadedFiles));
    formData.append("ai_service", "SUPPORT_ASSISTANT");
    formData.append("nowcerts_ams_url", "https://newtest.nowcerts.com");
    formData.append("rate_token", localStorage.getItem("rateToken") || "");

    try {
      const response = await fetch(
        "https://momentummyamp-stage.insurancegig.com/api/ams/chat",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            Accept: "text/event-stream",
          },
          body: formData,
        }
      );

      if (response.status === 401) {
        localStorage.clear();
        navigate("/");
        return;
      }

      if (!response.body) {
        throw new Error("Streaming not supported");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.startsWith("data:")) continue;

          const payload = event.replace("data:", "").trim();
          if (!payload) continue;

          let parsed: any;
          try {
            parsed = JSON.parse(payload);
          } catch {
            continue;
          }

          if (parsed.type === "answer" && parsed.response) {
            setMessages((prev) => {
              const updated = [...prev];
              updated[botIndex] = {
                ...updated[botIndex],
                text: updated[botIndex].text + parsed.response,
              };
              return updated;
            });
            scrollToBottom();
          }

          if (parsed.responseStreaming === false) {
            setMessages((prev) => {
              const updated = [...prev];
              const formatted = formatBotResponse(updated[botIndex].text);

              updated[botIndex] = {
                sender: "bot",
                text: formatted.message,
                followups: formatted.followups,
              };

              return updated;
            });

            reader.cancel();
            return;
          }
        }
      }
    } catch (error: any) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Something went wrong. Please try again." },
      ]);
    }
  };

  const scrollToBottom = (smooth = true) => {
    const el = messagesContainerRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    });
  };

  const formatBotResponse = (text: string) => {
    const start = "$$FollowupQuestions$$";
    const end = "$$FollowupQuestionsEND$$";

    if (!text.includes(start)) {
      return { message: text.trim(), followups: [] as string[] };
    }

    const [main, rest] = text.split(start);
    const followupBlock = rest.split(end)[0];

    const followups = followupBlock
      .replace(/\r?\n/g, " ")
      .split(/\d+\.\s+/)
      .map(q => q.trim())
      .filter(q => q.length > 0);

    return {
      message: main.trim(),
      followups,
    };
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && guid) {
      const formData = new FormData();
      formData.append('formFile', file);
      try {
        const response = await axios.post(
          `https://datafabricapi-stage.insurancegig.com/api/momentummyamp/upload-file/${guid}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            maxBodyLength: Infinity
          }
        );
        const fileUrl = response.data && response.data.fileUrl;
        const fileName = response.data && response.data.fileName;
        if (fileUrl && fileName) {
          setUploadedFiles(prev => [...prev, { fileName, fileURL: fileUrl }]);
        }
      } catch (error: any) {
        setMessages([
          ...messages,
          { sender: "user", text: `Uploaded file: ${file.name}` },
          { sender: "bot", text: `File upload failed. Please try again.` }
        ]);
      }
    }
    // Reset input value so same file can be selected again
    if (e.target) e.target.value = "";
  };

  const handleMicClick = () => {
    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };
    recognition.onerror = (event: any) => { };
    recognition.start();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend();
  };

  const handleAmpSelect = (amp: AMPConfig) => {
    setSelectedAmp(amp);
    setShowAmpPopover(false);

    // Generate new GUID and navigate to the selected AMP
    const newGuid = crypto.randomUUID();
    navigate(`/amp/${amp.id}/${newGuid}`);
  };

  return (
    <div className="supportbot-landing-layout">
      <HubHeader />
      <div className="supportbot-landing-body">
        <div className={`supportbot-sidebar-wrap ${collapsed ? "collapsed" : ""}`}> <HubSidebar pagename="supportbot-landing" collapsed={collapsed} onCollapseToggle={handleCollapseToggle} /> </div>
        <div className="p-3 d-flex flex-column h-100">
          <main className="supportbot-main-content">
            <main className={`amp-main-chat-container ${messages.length === 0 ? "amp-main-chat-container-empty" : ""}`} style={{ maxWidth: "1000px", width: "95%" }}>
              {(ampHistory && ampHistory.momentumMyAMPHistoryConversation && ampHistory.momentumMyAMPHistoryConversation.length > 0) ? (
                <div className="hub-messages" ref={messagesContainerRef}>
                  {ampHistory.momentumMyAMPHistoryConversation.map((conv: any, idx: any) => (
                    <React.Fragment key={conv.chatId || idx}>
                      <div className="hub-message hub-message-user" style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end", background: "none", boxShadow: "none", padding: "12px 0", marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ color: "#1f2937", fontSize: "15px", fontWeight: 500, lineHeight: "1.4" }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{conv.question}</ReactMarkdown>
                          </span>
                          <div style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: accentColor, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "11px", fontWeight: "600", flexShrink: 0 }}>U</div>
                        </div>
                      </div>
                      <div className="hub-message hub-message-bot" style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-start", background: "none", boxShadow: "none", padding: 0 }}>
                        {selectedAmp?.id === 'support-assistant' ? (
                          <img src={MomentumAILogoSmall} alt="AI" style={{ width: 28, height: 28, borderRadius: "8px", boxShadow: "none" }} />
                        ) : (
                          <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${selectedAmp?.color}15 0%, ${selectedAmp?.color}08 100%)`, borderRadius: '8px', border: `1px solid ${selectedAmp?.color}20`, flexShrink: 0 }}>
                            {selectedAmp && React.createElement(selectedAmp.icon as React.FC<IconBaseProps>, { size: 16, color: selectedAmp.color })}
                          </div>
                        )}
                        <span style={{ color: "#374151", fontWeight: 500, fontSize: "15px", lineHeight: "1.4" }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{conv.response}</ReactMarkdown>
                        </span>
                      </div>
                    </React.Fragment>
                  ))}
                  {/* Render current session messages after history */}
                  {messages.map((msg, idx) =>
                    msg.sender === "user" ? (
                      <div key={`user-${idx}`} className="hub-message hub-message-user" style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end", background: "none", boxShadow: "none", padding: "12px 0", marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ color: "#1f2937", fontSize: "15px", fontWeight: 500, lineHeight: "1.4" }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                          </span>
                          <div style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: accentColor, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "11px", fontWeight: "600", flexShrink: 0 }}>U</div>
                        </div>
                      </div>
                    ) : (
                      <div key={`bot-${idx}`} className="hub-message hub-message-bot" style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-start", background: "none", boxShadow: "none", padding: 0 }}>
                        {selectedAmp?.id === 'support-assistant' ? (
                          <img src={MomentumAILogoSmall} alt="AI" style={{ width: 28, height: 28, borderRadius: "8px", boxShadow: "none" }} />
                        ) : (
                          <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${selectedAmp?.color}15 0%, ${selectedAmp?.color}08 100%)`, borderRadius: '8px', border: `1px solid ${selectedAmp?.color}20`, flexShrink: 0 }}>
                            {selectedAmp && React.createElement(selectedAmp.icon as React.FC<IconBaseProps>, { size: 16, color: selectedAmp.color })}
                          </div>
                        )}
                        {(!msg.text || msg.text.length === 0) ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Skeleton width="500px" height="1rem" />
                            <Skeleton width="300px" height="1rem" />
                            <Skeleton width="150px" height="1rem" />
                          </div>
                        ) : (
                          <span style={{ color: "#374151", fontWeight: 500, fontSize: "15px", lineHeight: "1.4" }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                          </span>
                        )}
                      </div>
                    )
                  )}
                  <div ref={chatEndRef} />
                </div>
              ) : (
                messages.length === 0 ? (
                  <div className="hub-empty-state">
                    <div className="hub-empty-icon">
                      {selectedAmp?.id === 'support-assistant' ? (
                        <img src={MomentumAILogoSmall} alt="Momentum Logo" style={{ width: 95, height: 95 }} />
                      ) : (
                        <div style={{ width: 95, height: 95, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${selectedAmp?.color}15 0%, ${selectedAmp?.color}08 100%)`, borderRadius: '20px', border: `2px solid ${selectedAmp?.color}20` }}>
                          {selectedAmp && React.createElement(selectedAmp.icon as React.FC<IconBaseProps>, { size: 48, color: selectedAmp.color })}
                        </div>
                      )}
                    </div>
                    <h2 className="hub-empty-title">{selectedAmp?.emptyStateTitle || selectedAmp?.title || "What do you want to know?"}</h2>
                    <p className="hub-empty-subtitle">{selectedAmp?.emptyStateSubtitle || selectedAmp?.description || "Ask me anything and I'll help you find the answers"}</p>
                  </div>
                ) : (
                  <div className="hub-messages">
                    {messages.map((msg, idx) =>
                      msg.sender === "user" ? (
                        <div key={idx} className="hub-message hub-message-user" style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end", background: "none", boxShadow: "none", padding: "12px 0", marginBottom: "16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ color: "#1f2937", fontSize: "15px", fontWeight: 500, lineHeight: "1.4" }}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                            </span>
                            <div style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: accentColor, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "11px", fontWeight: "600", flexShrink: 0 }}>U</div>
                          </div>
                        </div>
                      ) : (
                        <div key={idx} className="hub-message hub-message-bot" style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-start", background: "none", boxShadow: "none", padding: 0 }}>
                          {selectedAmp?.id === 'support-assistant' ? (
                            <img src={MomentumAILogoSmall} alt="AI" style={{ width: 28, height: 28, borderRadius: "8px", boxShadow: "none" }} />
                          ) : (
                            <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${selectedAmp?.color}15 0%, ${selectedAmp?.color}08 100%)`, borderRadius: '8px', border: `1px solid ${selectedAmp?.color}20`, flexShrink: 0 }}>
                              {selectedAmp && React.createElement(selectedAmp.icon as React.FC<IconBaseProps>, { size: 16, color: selectedAmp.color })}
                            </div>
                          )}
                          {(!msg.text || msg.text.length === 0) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              <Skeleton width="500px" height="1rem" />
                              <Skeleton width="300px" height="1rem" />
                              <Skeleton width="150px" height="1rem" />
                            </div>
                          ) : (
                            <span style={{ color: "#374151", fontWeight: 500, fontSize: "15px", lineHeight: "1.4" }}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                            </span>
                          )}
                        </div>
                      )
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )
              )}


              <div className="hub-input-container">
                <div className="hub-input-inner">
                  <div className="hub-input-left-section">
                    <div className="amp-selector-wrap" ref={ampSelectorRef}>
                      <button
                        type="button"
                        className="amp-selector-btn"
                        onClick={() => setShowAmpPopover((v) => !v)}
                        title="Choose assistant"
                      >
                        <span className="amp-selector-icon">
                          {selectedAmp && React.createElement(selectedAmp.icon as React.FC<IconBaseProps>, {
                            size: 14,
                            color: selectedAmp.color
                          })}
                        </span>
                        <span className="amp-selector-label">
                          {selectedAmp?.title || "Select AMP"}
                        </span>
                        <span className="amp-selector-chevron">▾</span>
                      </button>
                      <div className="amp-selector-separator"></div>

                      {showAmpPopover && (
                        <div
                          className="amp-selector-popover"
                          ref={ampPopoverRef}
                        >
                          <div className="amp-options-grid">
                            {ampOptions.map((amp) => (
                              <button
                                key={amp.id}
                                type="button"
                                className={`amp-option-card ${amp.id === selectedAmp?.id ? "active" : ""
                                  }`}
                                onClick={() => handleAmpSelect(amp)}
                              >
                                <div className="amp-option-icon" style={{ color: amp.color }}>
                                  {React.createElement(amp.icon as React.FC<IconBaseProps>, {
                                    size: 16
                                  })}
                                </div>
                                <div className="amp-option-content">
                                  <span className="amp-option-title">
                                    {amp.title}
                                  </span>
                                  <p className="amp-option-desc">
                                    {amp.description}
                                  </p>
                                </div>
                                {amp.id === selectedAmp?.id && (
                                  <div className="amp-option-check">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20,6 9,17 4,12" />
                                    </svg>
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      className="hub-attach-btn hub-attach-purple"
                      onClick={handleUploadClick}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6b46c1"
                        strokeWidth="2"
                      >
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                      </svg>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="What do you want to know?"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    className="hub-input"
                  />

                  <div className="hub-actions">
                    <button
                      onClick={handleSend}
                      disabled={!input.trim()}
                      className={`hub-send-btn ${input.trim() ? "hub-send-btn-active" : ""
                        }`}
                      title="Send"
                    >
                      <Send size={22} />
                    </button>
                    <button
                      className="hub-mic-btn"
                      title="Voice input"
                      onClick={handleMicClick}
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6b46c1"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="9" y="2" width="6" height="12" rx="3" />
                        <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
                        <line x1="12" y1="22" x2="12" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <p className="hub-footer-text">
                By messaging Momentum, you agree to our{" "}
                <span className="hub-link">Terms</span> and{" "}
                <span className="hub-link">Privacy Policy</span>
              </p>
            </main>
          </main>
        </div>
      </div>
    </div>
  );
}
